import { CharacterRoster, portraitFileName } from './characters';
import { cutsceneFromJson, type Cutscene } from './cutscene';

const STORY_DIR = 'story/';
const PORTRAIT_DIR = `${STORY_DIR}portraits/`;
const BACKDROP_DIR = `${STORY_DIR}scenes/`;

const cutsceneCache = new Map<string, Promise<Cutscene>>();
let rosterCache: Promise<CharacterRoster> | null = null;

export function storyAssetUrl(fileName: string): string {
  return `${import.meta.env.BASE_URL}${STORY_DIR}${fileName}`;
}

export function portraitUrl(speakerId: string): string {
  return `${import.meta.env.BASE_URL}${PORTRAIT_DIR}${portraitFileName(speakerId)}`;
}

export function backdropUrl(cutsceneId: string): string {
  return `${import.meta.env.BASE_URL}${BACKDROP_DIR}${backdropFileName(cutsceneId)}`;
}

/** Both visits to a hideout room happen in the same place, so they share one image. */
export function backdropFileName(cutsceneId: string): string {
  return `${cutsceneId.replace(/^(hideout_[A-Za-z]+)_\d+$/, '$1')}.webp`;
}

export function loadCutscene(cutsceneId: string): Promise<Cutscene> {
  const cached = cutsceneCache.get(cutsceneId);
  if (cached) return cached;

  const pending = fetchCutscene(cutsceneId).catch((error: unknown) => {
    cutsceneCache.delete(cutsceneId);
    throw error;
  });
  cutsceneCache.set(cutsceneId, pending);
  return pending;
}

export function loadCharacters(): Promise<CharacterRoster> {
  rosterCache ??= fetchRoster().catch((error: unknown) => {
    rosterCache = null;
    throw error;
  });
  return rosterCache;
}

export function clearStoryCache(): void {
  cutsceneCache.clear();
  rosterCache = null;
}

async function fetchCutscene(cutsceneId: string): Promise<Cutscene> {
  const fileName = `${cutsceneId}.json`;
  return cutsceneFromJson(await fetchStoryJson(fileName), fileName);
}

async function fetchRoster(): Promise<CharacterRoster> {
  return CharacterRoster.fromJson(await fetchStoryJson('characters.json'), 'characters.json');
}

async function fetchStoryJson(fileName: string): Promise<unknown> {
  const url = storyAssetUrl(fileName);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load ${url} (HTTP ${response.status})`);
  }
  return response.json();
}
